// kpi-store kırılım set eylemleri dilimi (spec §6.3 DR6; kpi-store.ts 600 satır sınırı). Grup
// oluşturma sunucuda TEK tx'tir (grup + üyeler + set satırları + ham toplam → raporlanan): iyimser
// yazım yok, hata durumu değiştirmez (geri alınacak bir şey yok) ve çağırana fırlatılır — modal
// kodu `KPI_COPY.breakdown.errors`'a çevirir. Başarıda grid + set listesi + müşteri özeti yenilenir;
// dönüşen değer sayısı kalıcı banda (`breakdownConverted`, kullanıcı kapatana kadar) yazılır.
// Hesap grubu seçimi (IndicatorModal radyosu) set ağacı taslağından geçer — kaydet çubuğunun
// mevcut PATCH yolu (`calculationGroupMetricId`), burada ayrı eylem yok.
// Katalog (Task 15): "Kırılıma dönüştür" ve katalogdan grup ekleme de bu dilimde — ikisi de tek tx;
// dryRun hiçbir şey yenilemez, uygulamada açık set (varsa) grid + set listesi yenilenir (dönüşüm
// setleri de yazar); katalog listesini çağıran sayfa kendisi yeniden yükler. Set bandı açılmaz.

import type {
  KpiBreakdownConvertBody,
  KpiBreakdownConvertResult,
  KpiBreakdownGroupCreateBody,
  KpiBreakdownGroupCreateResult,
} from "@mahir/shared";
import { convertKpiBreakdown, createKpiBreakdownGroup } from "../api/kpi";

export interface KpiSetBreakdownState {
  /** set editörü kalıcı bilgi bandı: "N değer raporlanan değere dönüştü…" (yalnız N > 0) */
  breakdownConverted: { setId: string; values: number } | null;
  /** BreakdownGroupModal kaydı; `body.dryRun` → yalnız önizleme (hiçbir şey yenilenmez) */
  addBreakdownGroup: (
    totalMetricId: string,
    body: KpiBreakdownGroupCreateBody,
  ) => Promise<KpiBreakdownGroupCreateResult>;
  dismissBreakdownConverted: () => void;
  /** Katalog "Kırılıma dönüştür" (`body.dryRun` → önizleme); hata çağırana fırlatılır */
  convertCatalogBreakdown: (
    metricId: string,
    body: KpiBreakdownConvertBody,
  ) => Promise<KpiBreakdownConvertResult>;
  /** Katalogdan grup ekleme (sete satır yok); `body.dryRun` → önizleme */
  addCatalogBreakdownGroup: (
    totalMetricId: string,
    body: KpiBreakdownGroupCreateBody,
  ) => Promise<KpiBreakdownGroupCreateResult>;
}

interface Host {
  setId: string | null;
  refreshGrid: () => Promise<void>;
  reloadSets: () => Promise<void>;
  loadClients: () => Promise<void>;
}

export function createSetBreakdownSlice(
  set: (partial: Partial<KpiSetBreakdownState>) => void,
  get: () => Host,
): KpiSetBreakdownState {
  return {
    breakdownConverted: null,

    async addBreakdownGroup(totalMetricId, body) {
      const result = await createKpiBreakdownGroup(totalMetricId, body);
      if (result.dryRun) return result;
      const setId = body.setId ?? get().setId;
      if (setId && result.values > 0) set({ breakdownConverted: { setId, values: result.values } });
      const host = get();
      await Promise.all([host.refreshGrid(), host.reloadSets()]);
      void host.loadClients();
      return result;
    },

    dismissBreakdownConverted() {
      set({ breakdownConverted: null });
    },

    async convertCatalogBreakdown(metricId, body) {
      const result = await convertKpiBreakdown(metricId, body);
      if (!result.dryRun && !result.noop) await refreshOpenSet(get());
      return result;
    },

    async addCatalogBreakdownGroup(totalMetricId, body) {
      const result = await createKpiBreakdownGroup(totalMetricId, body);
      if (!result.dryRun) await refreshOpenSet(get());
      return result;
    },
  };
}

/** Katalog yazımından sonra açık set (varsa) — yenileme hatası katalog işlemini bozmaz. */
async function refreshOpenSet(host: Host): Promise<void> {
  if (!host.setId) return;
  await Promise.allSettled([host.refreshGrid(), host.reloadSets()]);
}
